import {
	animate,
	motion,
	useInView,
	useMotionValue,
	useReducedMotion,
	useTransform,
	type Variants,
} from "motion/react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { EASE } from "@/components/motion/motion";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

const Y0 = 2014;
/** Decimal year, month granularity. */
const toYear = (date: Date) => date.getFullYear() + date.getMonth() / 12 + 0.04;
/** Build date: identical on server and client, so the first render hydrates cleanly. */
const BUILD_TODAY = toYear(new Date(__BUILD_DATE__));
const Y1 = Math.max(2027.2, BUILD_TODAY + 0.45);

/** "Today" for ongoing bars: the build date, then the visitor's date once mounted. */
function useToday() {
	const [today, setToday] = useState(BUILD_TODAY);
	useEffect(() => setToday(Math.min(toYear(new Date()), Y1 - 0.3)), []);
	return today;
}

const ratio = (year: number) => (year - Y0) / (Y1 - Y0);
const pct = (year: number) => `${ratio(year) * 100}%`;

/** The print head crosses one year in this many seconds; bars print under it. */
const SECONDS_PER_YEAR = 0.07;
const HEAD_START = 0.15;
const delayOf = (year: number) => HEAD_START + (year - Y0) * SECONDS_PER_YEAR;

type Tone =
	| "light"
	| "learn"
	| "tone-1"
	| "tone-2"
	| "tone-3"
	| "product"
	| "beyond";
type BarKey =
	| "amplitude"
	| "selfTaught"
	| "silbo"
	| "linote"
	| "vaerdict"
	| "ccp"
	| "mobiapps"
	| "chaban"
	| "questovery"
	| "around";

type Item =
	| {
			kind: "bar";
			bar: BarKey;
			from: number;
			to: number;
			tone: Tone;
			label?: boolean;
	  }
	| {
			kind: "tag";
			bar: BarKey | "master";
			at: number;
			align?: "end";
			small?: boolean;
	  }
	| { kind: "dot"; at: number };

type Lane = { label?: "laser" | "code" | "products" | "around"; items: Item[] };

const buildLanes = (today: number): Lane[] => [
	{
		label: "laser",
		items: [
			{ kind: "dot", at: 2014.14 },
			{ kind: "tag", bar: "master", at: 2014.26, small: true },
			{ kind: "bar", bar: "amplitude", from: 2015, to: 2020, tone: "light" },
		],
	},
	{
		label: "code",
		items: [
			{ kind: "bar", bar: "selfTaught", from: 2015, to: 2020, tone: "learn" },
			{ kind: "bar", bar: "silbo", from: 2020, to: 2023, tone: "tone-1" },
			{ kind: "bar", bar: "linote", from: 2023, to: 2024, tone: "tone-3" },
			{ kind: "bar", bar: "vaerdict", from: 2024, to: today, tone: "tone-2" },
		],
	},
	{
		items: [
			{ kind: "tag", bar: "ccp", at: 2024.1, align: "end" },
			{
				kind: "bar",
				bar: "ccp",
				from: 2024.17,
				to: 2025.72,
				tone: "tone-3",
				label: false,
			},
			{
				kind: "bar",
				bar: "mobiapps",
				from: 2025.75,
				to: 2025.84,
				tone: "tone-1",
				label: false,
			},
			{ kind: "tag", bar: "mobiapps", at: 2025.93 },
		],
	},
	{
		label: "products",
		items: [
			{ kind: "bar", bar: "chaban", from: 2022, to: today, tone: "product" },
		],
	},
	{
		items: [
			{
				kind: "bar",
				bar: "questovery",
				from: 2024,
				to: today,
				tone: "product",
			},
		],
	},
	{
		label: "around",
		items: [
			{ kind: "bar", bar: "around", from: 2024, to: today, tone: "beyond" },
		],
	},
];

const TONE_CLASS: Record<Tone, string> = {
	light: "pastel-bar text-ink",
	learn: "border border-dashed border-ink-2 italic text-ink-2",
	"tone-1": "bg-tone-1 text-paper shadow-[inset_2px_0_0_var(--paper)]",
	"tone-2": "bg-tone-2 text-paper shadow-[inset_2px_0_0_var(--paper)]",
	"tone-3": "bg-tone-3 text-paper shadow-[inset_2px_0_0_var(--paper)]",
	product: "bg-sky text-ink",
	beyond: "bg-lilac text-ink",
};

/** Bars are printed at the head's speed, so the fill edge stays under the head. */
const printVariants: Variants = {
	hidden: { clipPath: "inset(0 100% 0 0)" },
	visible: ({ delay, duration }: { delay: number; duration: number }) => ({
		clipPath: "inset(0 0% 0 0)",
		transition: { duration, ease: "linear", delay },
	}),
};

const fadeVariants: Variants = {
	hidden: { opacity: 0 },
	visible: (delay: number) => ({
		opacity: 1,
		transition: { duration: 0.5, ease: EASE, delay },
	}),
};

/** "My path" chart: lanes of bars printed left to right when the chart enters the viewport. */
export function JourneyTimeline({ className }: { className?: string }) {
	const { t } = useI18n();
	const today = useToday();
	const years = Array.from(
		{ length: Math.floor(today) - Y0 + 1 },
		(_, i) => Y0 + i,
	);
	const lanes = buildLanes(today);
	const label = (bar: BarKey | "master") => t((t) => t.home.journey.bars[bar]);
	const laneLabel = (lane: NonNullable<Lane["label"]>) =>
		t((t) => t.home.journey.lanes[lane]);
	const chartRef = useRef<HTMLDivElement>(null);
	const inView = useInView(chartRef, {
		once: true,
		margin: "0px 0px -15% 0px",
	});

	return (
		<div className={cn("overflow-x-auto pb-2", className)}>
			<motion.div
				ref={chartRef}
				className="relative min-w-[44rem] pt-6"
				initial="hidden"
				animate={inView ? "visible" : "hidden"}
			>
				<div
					className="pointer-events-none absolute top-0 right-0 bottom-7 left-[4.75rem]"
					aria-hidden="true"
				>
					{years.map((year) => (
						<span
							key={year}
							className="absolute top-0 bottom-0 border-l border-dotted border-rule-2"
							style={{ left: pct(year) }}
						/>
					))}
					<span
						className="absolute top-0 bottom-0 border-l-2 border-ink"
						style={{ left: pct(2020) }}
					/>
					<motion.span
						className="absolute top-0 bottom-0 origin-top border-l border-dashed border-ink-2"
						style={{ left: pct(today) }}
						variants={fadeVariants}
						custom={1.1}
					>
						<span className="text-hand absolute top-0 right-1.5 text-lg leading-none whitespace-nowrap">
							{t((t) => t.home.journey.today)}
						</span>
					</motion.span>
				</div>

				<ScanHead active={inView} today={today} />

				<ul className="relative space-y-1.5">
					{lanes.map((lane, laneIndex) => (
						<li
							key={laneIndex}
							className={cn(
								"grid grid-cols-[4.75rem_1fr]",
								lane.label && laneIndex > 0 && "pt-3",
							)}
						>
							<span className="font-mono text-[0.72rem] leading-7 text-ink-3">
								{lane.label ? laneLabel(lane.label) : null}
							</span>
							<div className="relative h-7">
								{lane.items.map((item) => (
									<TimelineItem
										key={`${item.kind}-${"bar" in item ? item.bar : item.at}-${"from" in item ? item.from : ""}`}
										item={item}
										label={"bar" in item ? label(item.bar) : ""}
										delay={delayOf(
											"from" in item ? item.from : "at" in item ? item.at : Y0,
										)}
									/>
								))}
							</div>
						</li>
					))}
				</ul>

				<div className="relative mt-2 ml-[4.75rem] h-5" aria-hidden="true">
					{years.map((year) => (
						<span
							key={year}
							className={cn(
								"absolute top-0 pl-1 font-mono text-[0.68rem]",
								year === 2020 ? "font-semibold text-ink" : "text-ink-3",
							)}
							style={{ left: pct(year) }}
						>
							{year === Y0 || year === 2020 || year % 10 === 6
								? year
								: String(year).slice(2)}
						</span>
					))}
				</div>
			</motion.div>
		</div>
	);
}

function TimelineItem({
	item,
	label,
	delay,
}: {
	item: Item;
	label: string;
	delay: number;
}) {
	if (item.kind === "dot") {
		return (
			<span
				className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ink"
				style={{ left: pct(item.at) }}
				aria-hidden="true"
			/>
		);
	}

	if (item.kind === "tag") {
		return (
			<motion.span
				className={cn(
					"absolute top-0 font-mono leading-7 whitespace-nowrap",
					item.small
						? "text-[0.68rem] text-ink-2"
						: "text-[0.74rem] font-medium text-ink",
					item.align === "end" && "-translate-x-full pr-1.5",
				)}
				style={{ left: pct(item.at) }}
				variants={fadeVariants}
				custom={delay + 0.4}
			>
				{label}
			</motion.span>
		);
	}

	const style: React.CSSProperties = {
		left: pct(item.from),
		width: `calc(${pct(item.to)} - ${pct(item.from)})`,
	};
	const duration = (item.to - item.from) * SECONDS_PER_YEAR;

	return (
		<motion.span
			className={cn(
				"absolute top-0 flex h-full items-center overflow-hidden px-2 font-mono text-[0.74rem] font-medium whitespace-nowrap",
				TONE_CLASS[item.tone],
			)}
			style={style}
			variants={printVariants}
			custom={{ delay, duration }}
			title={item.label === false ? label : undefined}
		>
			{item.label === false ? null : label}
		</motion.span>
	);
}

/**
 * The print head: a pastel line sweeping from 2014 to today with the year it is
 * printing, then lifting off. Not rendered as visible state on the server.
 */
function ScanHead({ active, today }: { active: boolean; today: number }) {
	const reduced = useReducedMotion();
	const progress = useMotionValue(0);
	const opacity = useMotionValue(0);
	const end = ratio(today) * 100;
	const x = useTransform(progress, (p) => `${p * end}%`);
	const year = useTransform(progress, (p) =>
		Math.min(Math.floor(today), Math.floor(Y0 + p * (today - Y0))),
	);

	useEffect(() => {
		if (!active || reduced) return;
		const sweep = (today - Y0) * SECONDS_PER_YEAR;
		opacity.set(1);
		const run = animate(progress, 1, {
			duration: sweep,
			ease: "linear",
			delay: HEAD_START,
		});
		const lift = animate(opacity, 0, {
			duration: 0.5,
			ease: EASE,
			delay: HEAD_START + sweep + 0.35,
		});
		return () => {
			run.stop();
			lift.stop();
		};
	}, [active, reduced, today, progress, opacity]);

	return (
		<motion.div
			className="pointer-events-none absolute top-0 right-0 bottom-7 left-[4.75rem] z-10"
			style={{ x, opacity }}
			aria-hidden="true"
		>
			<span className="pastel-bar absolute top-5 bottom-0 left-0 w-[2px] -translate-x-1/2 shadow-[0_0_10px_var(--lilac)]" />
			<motion.span className="absolute top-0 left-0 -translate-x-1/2 bg-ink px-1 font-mono text-[0.62rem] leading-4 text-paper tabular-nums">
				{year}
			</motion.span>
		</motion.div>
	);
}
